import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Plus, Upload, Loader2, FileVideo, Image as ImageIcon, Sparkles } from 'lucide-react'

interface Props {
  isOpen: boolean
  onClose: () => void
  onSubmit: (files: File[]) => void
  isSubmitting?: boolean
}

const ACCEPTED_TYPE_PREFIXES = ['image/', 'video/']

function isAcceptedFile(file: File): boolean {
  return ACCEPTED_TYPE_PREFIXES.some(p => file.type.startsWith(p))
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function CreateAdUnitsAssetsModal({ isOpen, onClose, onSubmit, isSubmitting = false }: Props) {
  const [files, setFiles] = useState<File[]>([])
  const [previews, setPreviews] = useState<string[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [rejectedCount, setRejectedCount] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen) {
      setFiles([])
      setPreviews(prev => {
        prev.forEach(u => URL.revokeObjectURL(u))
        return []
      })
      setIsDragging(false)
      setRejectedCount(0)
    }
  }, [isOpen])

  useEffect(() => {
    return () => {
      previews.forEach(u => URL.revokeObjectURL(u))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) {
        onClose()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, isSubmitting, onClose])

  const addFiles = (incoming: FileList | File[]) => {
    const arr = Array.from(incoming)
    const accepted = arr.filter(isAcceptedFile)
    const rejected = arr.length - accepted.length
    if (rejected > 0) setRejectedCount(c => c + rejected)
    if (!accepted.length) return
    setFiles(prev => [...prev, ...accepted])
    setPreviews(prev => [
      ...prev,
      ...accepted.map(f => (f.type.startsWith('image/') ? URL.createObjectURL(f) : '')),
    ])
  }

  const removeFile = (idx: number) => {
    const url = previews[idx]
    if (url) URL.revokeObjectURL(url)
    setFiles(prev => prev.filter((_, i) => i !== idx))
    setPreviews(prev => prev.filter((_, i) => i !== idx))
  }

  const handleSubmit = () => {
    if (isSubmitting) return
    onSubmit(files)
  }

  const handleSkip = () => {
    if (isSubmitting) return
    onClose()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 dark:bg-black/80 backdrop-blur-sm p-4"
          onClick={handleSkip}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            onClick={e => e.stopPropagation()}
            className="bg-white dark:bg-gray-950 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white flex flex-col overflow-hidden w-full max-w-2xl max-h-[85vh]"
            layout
          >
            <div className="relative flex-shrink-0 px-8 pt-6 pb-5 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-primary-100 via-white to-white dark:from-primary-900/40 dark:via-gray-950 dark:to-gray-950">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold tracking-[0.18em] text-primary-600 dark:text-primary-400 uppercase flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3" />
                    Optional
                  </div>
                  <h2 className="text-2xl font-bold mt-1">Upload assets</h2>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Attach product images or reference videos to include with your ad unit creation. Skip to generate without assets.
                  </p>
                </div>
                <button
                  onClick={handleSkip}
                  aria-label="Close"
                  title="Close"
                  disabled={isSubmitting}
                  className="h-9 w-9 flex-shrink-0 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/80 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white flex items-center justify-center transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="h-4 w-4" strokeWidth={2.5} />
                </button>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin p-6">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[11px] font-semibold tracking-wider uppercase text-gray-600 dark:text-gray-400">
                  Assets
                </label>
                {files.length > 0 && (
                  <span className="text-[11px] text-gray-500">
                    {files.length} file{files.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>

              <div
                onDragOver={e => {
                  e.preventDefault()
                  setIsDragging(true)
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={e => {
                  e.preventDefault()
                  setIsDragging(false)
                  if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files)
                }}
                className={`rounded-xl border-2 border-dashed p-4 transition-colors ${
                  isDragging
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/30'
                }`}
              >
                {files.length === 0 ? (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full flex flex-col items-center justify-center gap-2 py-8 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    <div className="h-12 w-12 rounded-full bg-gray-100 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 flex items-center justify-center">
                      <Upload className="h-5 w-5" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        Drag &amp; drop assets here or click to browse
                      </p>
                      <p className="text-[11px] text-gray-500 mt-1">
                        Images and videos supported
                      </p>
                    </div>
                  </button>
                ) : (
                  <div className="flex flex-wrap gap-3 content-start">
                    {files.map((f, i) => (
                      <div key={`${f.name}-${i}`} className="relative group w-24">
                        <div className="w-24 h-24 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 overflow-hidden flex items-center justify-center">
                          {f.type.startsWith('image/') && previews[i] ? (
                            <img src={previews[i]} alt={f.name} className="w-full h-full object-cover" />
                          ) : f.type.startsWith('video/') ? (
                            <FileVideo className="h-7 w-7 text-gray-500" />
                          ) : (
                            <ImageIcon className="h-7 w-7 text-gray-500" />
                          )}
                        </div>
                        <div className="mt-1 text-[10px] text-gray-700 dark:text-gray-400 truncate" title={f.name}>
                          {f.name}
                        </div>
                        <div className="text-[10px] text-gray-500 dark:text-gray-600">{formatBytes(f.size)}</div>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          disabled={isSubmitting}
                          className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-gray-500 dark:bg-gray-700 hover:bg-gray-600 dark:hover:bg-gray-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-0"
                          aria-label={`Remove ${f.name}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                      aria-label="Add more files"
                    >
                      <Plus className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>

              {rejectedCount > 0 && (
                <p className="mt-2 text-[11px] text-amber-600 dark:text-amber-400">
                  {rejectedCount} file{rejectedCount !== 1 ? 's were' : ' was'} skipped. Only images and videos are supported.
                </p>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                className="hidden"
                onChange={e => {
                  if (e.target.files?.length) addFiles(e.target.files)
                  e.target.value = ''
                }}
              />
            </div>

            <div className="flex items-center justify-between gap-3 px-8 py-3 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 flex-shrink-0">
              <div className="min-w-0 text-xs text-gray-600 dark:text-gray-400">
                {files.length > 0 ? (
                  <span>
                    <span className="font-medium text-gray-900 dark:text-white">{files.length}</span> asset
                    {files.length === 1 ? '' : 's'} ready to upload
                  </span>
                ) : (
                  <span>No assets selected</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSkip}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Skip
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3.5 w-3.5" />
                      {files.length > 0 ? 'Create ad units' : 'Create without assets'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
